/**
 * Frontend Logger Utility
 * Supports configurable severity levels and structured logging.
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4
}

interface LogEntry {
  timestamp: string;
  level: string;
  message: string;
  data?: unknown;
  correlationId?: string;
}

class Logger {
  private level: LogLevel = LogLevel.INFO;
  private correlationId: string | null = null;

  constructor() {
    const envLevel = process.env.REACT_APP_LOG_LEVEL;
    if (envLevel) {
      this.setLevel(envLevel as keyof typeof LogLevel);
    }
  }

  public setLevel(level: keyof typeof LogLevel | LogLevel) {
    if (typeof level === 'string') {
      this.level = LogLevel[level] || LogLevel.INFO;
    } else {
      this.level = level;
    }
  }

  public setCorrelationId(id: string | null) {
    this.correlationId = id;
  }

  private log(level: LogLevel, message: string, data?: unknown) {
    if (level < this.level) return;

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level: LogLevel[level],
      message,
      data,
      correlationId: this.correlationId || undefined
    };

    // In a real app, you might send this to an endpoint or App Insights
    // For now, we format it nicely for the console
    const style = this.getStyle(level);
    console.log(`%c[${entry.level}] ${entry.message}`, style, data || '');
  }

  private getStyle(level: LogLevel): string {
    switch (level) {
      case LogLevel.DEBUG: return 'color: gray';
      case LogLevel.INFO: return 'color: blue';
      case LogLevel.WARN: return 'color: orange';
      case LogLevel.ERROR: return 'color: red; font-weight: bold';
      default: return '';
    }
  }

  public debug(message: string, data?: unknown) { this.log(LogLevel.DEBUG, message, data); }
  public info(message: string, data?: unknown) { this.log(LogLevel.INFO, message, data); }
  public warn(message: string, data?: unknown) { this.log(LogLevel.WARN, message, data); }
  public error(message: string, data?: unknown) { this.log(LogLevel.ERROR, message, data); }
}

export const logger = new Logger();
